import axiosInstance from "../common/axiosInstance";
import { create } from "zustand";
import {
  HazardousMaterialType,
  SafetyComplianceType,
  StorageHandlingType,
  TrackingMovementType,
  InspectionComplianceType,
  DisposalEventType,
  ReportResultType,
  SearchFiltersType,
  IncidentSpillReportType,
  HazardousWasteLogType,
} from "../types/api";

interface HazardousMaterialState {
  hazardousMaterials: HazardousMaterialType[];
  materialDataMap: Map<string, string>;
  loading: boolean;
  error: boolean;

  fetchAllHazardousMaterials: () => Promise<boolean>;
  createHazardousMaterial: (data: HazardousMaterialType) => Promise<boolean>;
  updateHazardousMaterial: (
    data: HazardousMaterialType,
    id: string
  ) => Promise<boolean>;
  deleteHazardousMaterial: (id: string) => Promise<boolean>;
}

export const useHazardousMaterialStore = create<HazardousMaterialState>(
  (set, get) => ({
    hazardousMaterials: [],
    materialDataMap: new Map(),
    loading: false,
    error: false,

    fetchAllHazardousMaterials: async () => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/hazardousmaterials/all`
        );
        const hazardousMaterials = response?.data?.data || [];
        const materialDataMap = new Map<string, string>(
          hazardousMaterials.map((m: HazardousMaterialType) => [
            m._id,
            m.materialName,
          ])
        );
        set({
          hazardousMaterials,
          materialDataMap,
          loading: false,
          error: false,
        });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    createHazardousMaterial: async (data: HazardousMaterialType) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.post(`/api/hazardousmaterials/create`, data);
        await get().fetchAllHazardousMaterials();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    updateHazardousMaterial: async (
      data: HazardousMaterialType,
      id: string
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.put(`/api/hazardousmaterials/${id}`, data);
        await get().fetchAllHazardousMaterials();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    deleteHazardousMaterial: async (id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.delete(`/api/hazardousmaterials/${id}`);
        await get().fetchAllHazardousMaterials();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
  })
);

interface StorageHandlingState {
  storageHandlingRecords: StorageHandlingType[];
  loading: boolean;
  error: boolean;

  fetchAllStorageHandling: () => Promise<boolean>;
  createStorageHandling: (data: StorageHandlingType) => Promise<boolean>;
  updateStorageHandling: (
    data: StorageHandlingType,
    id: string
  ) => Promise<boolean>;
  deleteStorageHandling: (id: string) => Promise<boolean>;
}

export const useStorageHandlingStore = create<StorageHandlingState>(
  (set, get) => ({
    storageHandlingRecords: [],
    loading: false,
    error: false,

    fetchAllStorageHandling: async () => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/storagehandling/all`
        );
        set({
          storageHandlingRecords: response?.data?.data || [],
          loading: false,
          error: false,
        });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    createStorageHandling: async (data: StorageHandlingType) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.post(`/api/storagehandling/create`, data);
        await get().fetchAllStorageHandling();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    updateStorageHandling: async (data: StorageHandlingType, id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.put(`/api/storagehandling/${id}`, data);
        await get().fetchAllStorageHandling();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    deleteStorageHandling: async (id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.delete(`/api/storagehandling/${id}`);
        await get().fetchAllStorageHandling();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
  })
);

interface SafetyComplianceState {
  safetyComplianceRecords: SafetyComplianceType[];
  loading: boolean;
  error: boolean;

  fetchAllSafetyCompliance: () => Promise<boolean>;
  createSafetyCompliance: (data: SafetyComplianceType) => Promise<boolean>;
  updateSafetyCompliance: (
    data: SafetyComplianceType,
    id: string
  ) => Promise<boolean>;
  deleteSafetyCompliance: (id: string) => Promise<boolean>;
}

export const useSafetyComplianceStore = create<SafetyComplianceState>(
  (set, get) => ({
    safetyComplianceRecords: [],
    loading: false,
    error: false,

    fetchAllSafetyCompliance: async () => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/safetycompliance/all`
        );
        set({
          safetyComplianceRecords: response?.data?.data || [],
          loading: false,
          error: false,
        });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    createSafetyCompliance: async (data: SafetyComplianceType) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.post(`/api/safetycompliance/create`, data);
        await get().fetchAllSafetyCompliance();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    updateSafetyCompliance: async (data: SafetyComplianceType, id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.put(`/api/safetycompliance/${id}`, data);
        await get().fetchAllSafetyCompliance();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    deleteSafetyCompliance: async (id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.delete(`/api/safetycompliance/${id}`);
        await get().fetchAllSafetyCompliance();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
  })
);

interface TrackingMovementState {
  trackingMovementRecords: TrackingMovementType[];
  loading: boolean;
  error: boolean;

  fetchAllTrackingMovement: () => Promise<boolean>;
  createTrackingMovement: (data: TrackingMovementType) => Promise<boolean>;
  updateTrackingMovement: (
    data: TrackingMovementType,
    id: string
  ) => Promise<boolean>;
  deleteTrackingMovement: (id: string) => Promise<boolean>;
}

export const useTrackingMovementStore = create<TrackingMovementState>(
  (set, get) => ({
    trackingMovementRecords: [],
    loading: false,
    error: false,

    fetchAllTrackingMovement: async () => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/trackingmovement/all`
        );
        set({
          trackingMovementRecords: response?.data?.data || [],
          loading: false,
          error: false,
        });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    createTrackingMovement: async (data: TrackingMovementType) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.post(`/api/trackingmovement/create`, data);
        await get().fetchAllTrackingMovement();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    updateTrackingMovement: async (data: TrackingMovementType, id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.put(`/api/trackingmovement/${id}`, data);
        await get().fetchAllTrackingMovement();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    deleteTrackingMovement: async (id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.delete(`/api/trackingmovement/${id}`);
        await get().fetchAllTrackingMovement();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
  })
);

interface InspectionComplianceState {
  inspectionComplianceRecords: InspectionComplianceType[];
  loading: boolean;
  error: boolean;

  fetchAllInspectionCompliance: () => Promise<boolean>;
  createInspectionCompliance: (
    data: InspectionComplianceType
  ) => Promise<boolean>;
  updateInspectionCompliance: (
    data: InspectionComplianceType,
    id: string
  ) => Promise<boolean>;
  deleteInspectionCompliance: (id: string) => Promise<boolean>;
}

export const useInspectionComplianceStore = create<InspectionComplianceState>(
  (set, get) => ({
    inspectionComplianceRecords: [],
    loading: false,
    error: false,

    fetchAllInspectionCompliance: async () => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/inspectioncompliance/all`
        );
        set({
          inspectionComplianceRecords: response?.data?.data || [],
          loading: false,
          error: false,
        });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    createInspectionCompliance: async (data: InspectionComplianceType) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.post(
          `/api/inspectioncompliance/create`,
          data
        );
        await get().fetchAllInspectionCompliance();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    updateInspectionCompliance: async (
      data: InspectionComplianceType,
      id: string
    ) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.put(`/api/inspectioncompliance/${id}`, data);
        await get().fetchAllInspectionCompliance();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    deleteInspectionCompliance: async (id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.delete(`/api/inspectioncompliance/${id}`);
        await get().fetchAllInspectionCompliance();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
  })
);

interface DisposalManagementState {
  disposalEvents: DisposalEventType[];
  loading: boolean;
  error: boolean;

  fetchAllDisposalEvents: () => Promise<boolean>;
  createDisposalEvent: (data: DisposalEventType) => Promise<boolean>;
  updateDisposalEvent: (
    data: DisposalEventType,
    id: string
  ) => Promise<boolean>;
  deleteDisposalEvent: (id: string) => Promise<boolean>;
}

export const useDisposalManagementStore = create<DisposalManagementState>(
  (set, get) => ({
    disposalEvents: [],
    loading: false,
    error: false,

    fetchAllDisposalEvents: async () => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/disposalmanagement/all`
        );
        set({
          disposalEvents: response?.data?.data || [],
          loading: false,
          error: false,
        });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    createDisposalEvent: async (data: DisposalEventType) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.post(`/api/disposalmanagement/create`, data);
        await get().fetchAllDisposalEvents();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    updateDisposalEvent: async (data: DisposalEventType, id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.put(`/api/disposalmanagement/${id}`, data);
        await get().fetchAllDisposalEvents();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    deleteDisposalEvent: async (id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.delete(`/api/disposalmanagement/${id}`);
        await get().fetchAllDisposalEvents();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
  })
);

interface HazardousWasteState {
  wasteLogs: HazardousWasteLogType[];
  loading: boolean;
  error: boolean;

  fetchAllWasteLogs: () => Promise<boolean>;
  createWasteLog: (data: HazardousWasteLogType) => Promise<boolean>;
  updateWasteLog: (data: HazardousWasteLogType, id: string) => Promise<boolean>;
  deleteWasteLog: (id: string) => Promise<boolean>;
}

export const useHazardousWasteStore = create<HazardousWasteState>(
  (set, get) => ({
    wasteLogs: [],
    loading: false,
    error: false,

    fetchAllWasteLogs: async () => {
      set({ loading: true, error: false });
      try {
        const response = await axiosInstance.get(
          `/api/hazardouswaste/all`
        );
        set({
          wasteLogs: response?.data?.data || [],
          loading: false,
          error: false,
        });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    createWasteLog: async (data: HazardousWasteLogType) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.post(`/api/hazardouswaste/create`, data);
        await get().fetchAllWasteLogs();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    updateWasteLog: async (data: HazardousWasteLogType, id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.put(`/api/hazardouswaste/${id}`, data);
        await get().fetchAllWasteLogs();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },

    deleteWasteLog: async (id: string) => {
      set({ loading: true, error: false });
      try {
        await axiosInstance.delete(`/api/hazardouswaste/${id}`);
        await get().fetchAllWasteLogs();
        set({ loading: false, error: false });
        return true;
      } catch (error) {
        set({ error: true, loading: false });
        return false;
      }
    },
  })
);

interface SearchReportingState {
  reportResults: ReportResultType[];
  loading: boolean;
  error: boolean;

  runSearchReport: (filters: SearchFiltersType) => Promise<ReportResultType[]>;
}

export const useSearchReportingStore = create<SearchReportingState>((set) => ({
  reportResults: [],
  loading: false,
  error: false,

  runSearchReport: async (filters: SearchFiltersType) => {
    set({ loading: true, error: false });
    try {
      const response = await axiosInstance.post(
        `/api/searchreporting`,
        filters
      );
      if (response.data.success) {
        set({ reportResults: response.data.data, loading: false });
        return response.data.data;
      } else {
        set({ error: true, loading: false });
        return [];
      }
    } catch (error) {
      set({ error: true, loading: false });
      return [];
    }
  },
}));

interface IncidentSpillStore {
  incidentReports: IncidentSpillReportType[];
  inventoryMaterials: { id: string; name: string }[];
  users: { id: string; name: string }[];
  loading: boolean;
  error: boolean;

  fetchAllReports: () => Promise<boolean>;
  createReport: (data: IncidentSpillReportType) => Promise<boolean>;
  updateReport: (data: IncidentSpillReportType, id: string) => Promise<boolean>;
  deleteReport: (id: string) => Promise<boolean>;
  fetchInventoryMaterials: () => Promise<boolean>;
  fetchUsers: () => Promise<boolean>;
}

export const useIncidentSpillStore = create<IncidentSpillStore>((set, get) => ({
  incidentReports: [],
  inventoryMaterials: [],
  users: [],
  loading: false,
  error: false,

  fetchAllReports: async () => {
    set({ loading: true, error: false });
    try {
      const res = await axiosInstance.get(`/api/incidentspill/all`);
      set({
        incidentReports: res.data.data || [],
        loading: false,
        error: false,
      });
      return true;
    } catch (e) {
      set({ error: true, loading: false });
      return false;
    }
  },

  createReport: async (data) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.post(`/api/incidentspill/create`, data);
      await get().fetchAllReports();
      set({ loading: false, error: false });
      return true;
    } catch (e) {
      set({ error: true, loading: false });
      return false;
    }
  },

  updateReport: async (data, id) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.put(`/api/incidentspill/${id}`, data);
      await get().fetchAllReports();
      set({ loading: false, error: false });
      return true;
    } catch (e) {
      set({ error: true, loading: false });
      return false;
    }
  },

  deleteReport: async (id) => {
    set({ loading: true, error: false });
    try {
      await axiosInstance.delete(`/api/incidentspill/${id}`);
      await get().fetchAllReports();
      set({ loading: false, error: false });
      return true;
    } catch (e) {
      set({ error: true, loading: false });
      return false;
    }
  },

  fetchInventoryMaterials: async () => {
    try {
      const res = await axiosInstance.get(`/api/inventory/materials`);
      set({ inventoryMaterials: res.data.data || [] });
      return true;
    } catch {
      return false;
    }
  },

  fetchUsers: async () => {
    try {
      const res = await axiosInstance.get(`/api/users/all`);
      set({ users: res.data.data || [] });
      return true;
    } catch {
      return false;
    }
  },
}));
